import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { DatabaseZap, Upload, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const POLL_MS = 2000;

export function DataImportCard() {
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [job, setJob] = useState(null);       // { status, log, report, error, db, filename }
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    setFile(null);
    setConfirmText("");
    setJob(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const poll = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await apiClient.get(`/admin/import-sql/status/${jobId}`);
        setJob(data);
        if (["done", "done_with_errors", "failed"].includes(data.status)) {
          clearInterval(pollRef.current);
          setBusy(false);
          if (data.status === "done") toast.success("Import complete.");
          else if (data.status === "done_with_errors") toast.warning("Import finished with some table errors.");
          else toast.error("Import failed.");
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setBusy(false);
        toast.error(getApiErrorMessage(e, "Lost track of the import job."));
      }
    }, POLL_MS);
  };

  const start = async () => {
    if (!file) { toast.error("Choose a .sql dump file first."); return; }
    if (confirmText !== "REPLACE") { toast.error('Type REPLACE to confirm.'); return; }
    setBusy(true);
    setJob({ status: "uploading", log: [], report: null });
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("confirm", "REPLACE");
      const { data } = await apiClient.post("/admin/import-sql", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob({ status: "running", log: [`Uploaded ${data.filename} → importing into "${data.db}"...`], report: null });
      poll(data.job_id);
    } catch (e) {
      setBusy(false);
      setJob(null);
      toast.error(getApiErrorMessage(e, "Could not start the import."));
    }
  };

  const done = job && ["done", "done_with_errors", "failed"].includes(job.status);
  const running = busy || (job && ["uploading", "queued", "running"].includes(job.status));

  return (
    <Card data-testid="settings-data-import-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <DatabaseZap size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Database Import</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Upload a MySQL/MariaDB <code className="rounded bg-background px-1">.sql</code> dump (e.g. from phpMyAdmin)
              and load it straight into the live database — customers, subscriptions, payments, deliveries and more.
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2.5 text-xs text-danger" data-testid="settings-data-import-warning">
          <AlertTriangle size={15} strokeWidth={1.75} className="mt-0.5 shrink-0" />
          <span>
            This <strong>replaces</strong> every table found in the dump — existing data in those tables is dropped
            and reloaded. Take a backup first. You may need to sign in again afterwards.
          </span>
        </div>

        {!done && (
          <div className="mt-4 space-y-3">
            <Input
              ref={fileRef}
              type="file"
              disabled={running}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="settings-data-import-file-input"
            />
            <p className="text-[11px] text-text-muted">
              Any SQL dump file is accepted — even if it was renamed on download (e.g. "tiffidb (1).sql 2"). It's checked by its contents, not its extension.
            </p>
            {file && (
              <p className="text-xs text-text-muted" data-testid="settings-data-import-filename">
                Selected: {file.name} ({(file.size / 1048576).toFixed(1)} MB)
              </p>
            )}
            <div>
              <label className="text-xs font-medium text-text-main">Type <span className="font-mono">REPLACE</span> to confirm</label>
              <Input
                value={confirmText}
                disabled={running}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="REPLACE"
                className="mt-1 max-w-[200px]"
                data-testid="settings-data-import-confirm-input"
              />
            </div>
            <Button
              onClick={start}
              disabled={running || !file || confirmText !== "REPLACE"}
              data-testid="settings-data-import-start-button"
            >
              {running ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Upload size={16} strokeWidth={1.75} className="mr-1" />}
              {running ? "Importing…" : "Start import"}
            </Button>
          </div>
        )}

        {job && (job.log?.length > 0 || job.report || job.error) && (
          <div className="mt-4" data-testid="settings-data-import-progress">
            {running && (
              <p className="mb-2 flex items-center gap-1.5 text-xs text-text-muted">
                <Loader2 size={13} className="animate-spin" /> Import running — this can take a minute for large dumps…
              </p>
            )}
            {job.log?.length > 0 && (
              <pre className="max-h-48 overflow-auto rounded-lg border border-border bg-background p-3 text-[11px] leading-relaxed text-text-muted" data-testid="settings-data-import-log">
                {job.log.join("\n")}
              </pre>
            )}
            {job.report && (
              <div className="mt-3 rounded-lg border border-success/30 bg-success-bg p-3 text-xs text-success" data-testid="settings-data-import-report">
                <p className="flex items-center gap-1.5 font-semibold">
                  <CheckCircle2 size={14} /> Imported {job.report.total_documents?.toLocaleString()} records across {job.report.tables} tables into "{job.report.db}".
                </p>
                {Object.keys(job.report.failures || {}).length > 0 && (
                  <p className="mt-1 text-danger">Failed tables: {Object.keys(job.report.failures).join(", ")}</p>
                )}
              </div>
            )}
            {job.error && (
              <p className="mt-2 text-xs text-danger" data-testid="settings-data-import-error">Error: {job.error}</p>
            )}
            {done && (
              <Button variant="outline" size="sm" className="mt-3" onClick={reset} data-testid="settings-data-import-reset-button">
                Import another file
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
